
import api from './APIConfig';
import { ResponseObrasMap, ResponseError, ObraProperties } from '../types/ObraMap';
import {
  ObraBim,
  ObraClasificacion,
  ClientsDetails,
  DescriptionDetails,
  EsgDetails,
  ObraContrato,
  LocationDetails,
  GeneralDataDetails,
  ObraSocios,
  WandaProject,
  ObraPresupuesto,
  DatesDetails,
  TechnicalAspectsDetails,
} from '../types/ObraDetails';
import { SelectOption, Option, OptionBoolean, MasterPage, RemoteMasterOption } from '../types/Option';
import ENDPOINTS from '../config/APIRoutes';
import { MASTERS_PATHS, FILTERS_TYPES, MASTERS_RESPONSE_SCHEMA, ACCUMULATIVE_NUMBER_FILTERS } from '../config/APIConfig';
import { FilterValue } from '../types/FiltersTypes';
import { AdminAssignmentCandidatesPage, AdminUsersPage, CenterParams, Usuario } from '../types/Admin';
import FILTER_TYPES from '../components/Filters/filterTypes';
import { TFunction } from 'i18next';
import { ExportJob, ExportJobDownload, ExportJobPage } from '../types/ExportJob';
import { ProjectCapabilities } from '../types/Authorization';
import { NotificationPage } from '../types/Notification';
import { TipoFoto } from '../types/ObraDetails';
import { filterMeaningfulOptions } from '../utils/filterMeaninglessOptions';

type FilterState = Record<string, FilterValue[]>;
type MasterRecord = Record<string, unknown>;
type MasterSchema = Record<string, string>;

const asMasterRecords = (results: unknown): MasterRecord[] =>
  Array.isArray(results) ? results.filter((result): result is MasterRecord => !!result && typeof result === 'object') : [];

const masterString = (record: MasterRecord, key: string): string => {
  const value = record[key];
  return value === undefined || value === null ? '' : String(value);
};

const masterNumber = (record: MasterRecord, key: string): number => Number(masterString(record, key));

class ObrasAPI {
  private static instance: ObrasAPI;

  private hasInvalidCenterAutocompleteLabel(name?: string, code?: string): boolean {
    if (!name) return true;

    const trimmedName = name.trim();
    const normalizedName = trimmedName.toLowerCase();
    const normalizedCode = code?.trim().toLowerCase();

    return (
      normalizedName.length === 0
      || normalizedName === '-'
      || (normalizedCode !== undefined && (
        normalizedName === normalizedCode
        || normalizedName === `${normalizedCode} -`
      ))
    );
  }

  private async enrichCenterAutocompleteResults(results: Array<{ code: string; name: string }>) {
    return await Promise.all(results.map(async (result) => {
      if (!this.hasInvalidCenterAutocompleteLabel(result.name, result.code)) {
        return result;
      }

      try {
        const obra = await this.getObraPropertiesAPI(result.code);
        return {
          ...result,
          name: obra?.nomInternoCentro ? `${result.code} - ${obra.nomInternoCentro}` : result.name,
        };
      } catch {
        return result;
      }
    }));
  }

  private getLanguage(): string {
    return localStorage.getItem('language') && localStorage.getItem('language') !== '' ? localStorage.getItem('language') || 'es' : 'es';
  }

  public static getInstance(): ObrasAPI {
    if (!ObrasAPI.instance) {
      ObrasAPI.instance = new ObrasAPI();
    }
    return ObrasAPI.instance;
  }

  private deleteCombinedFilters(filtersToApply: FilterState, filters: FilterState, filter: string):void {
    const types = FILTER_TYPES as unknown as Record<string, string>;
    if (Object.keys(filters).includes(types[`exact${filter}`]) && filters[types[`exact${filter}`]].length > 0) {
      delete filtersToApply[types[`exact${filter}`]];
      filtersToApply[types[`from${filter}`]] = filters[types[`exact${filter}`]];
      filtersToApply[types[`to${filter}`]] = filters[types[`exact${filter}`]];
    }
  }

  private bimRequirementsFilters(filtersToApply: FilterState, filters: FilterState):void {
    const types = FILTER_TYPES as unknown as Record<string, string>;
    if(filters.requirements && filters.requirements.length > 0){
      const newFilter: FilterValue[] = filters.requirements.flatMap((obj) => (obj.codigos || []).map((codigo) => ({
        code: String(codigo),
        name: obj.name
      })));
      filtersToApply[types.requirements] = newFilter;
    }
  }

  private generateFiltersPath(filters: FilterState = {}): string {
    const filtersToApply = {...filters};
    if (!filters) return `idioma=${this.getLanguage()}`;
    this.deleteCombinedFilters(filtersToApply, filters, FILTER_TYPES.adjudicationAmount);
    this.deleteCombinedFilters(filtersToApply, filters, FILTER_TYPES.accionaParticipation);
    this.deleteCombinedFilters(filtersToApply, filters, FILTER_TYPES.protectedArea);
    this.deleteCombinedFilters(filtersToApply, filters, FILTER_TYPES.protectedProjectArea);
    this.deleteCombinedFilters(filtersToApply, filters, FILTER_TYPES.accionaBudget);
    this.deleteCombinedFilters(filtersToApply, filters, FILTER_TYPES.updatedAccionaBudget);
    this.deleteCombinedFilters(filtersToApply, filters, FILTER_TYPES.updatedBudget);
    this.deleteCombinedFilters(filtersToApply, filters, FILTER_TYPES.finalAccionaBudget);
    this.deleteCombinedFilters(filtersToApply, filters, FILTER_TYPES.finalBudget);
    this.deleteCombinedFilters(filtersToApply, filters, FILTER_TYPES.warrantyPeriod);
    this.deleteCombinedFilters(filtersToApply, filters, FILTER_TYPES.percentPartnerParticipation);
    this.deleteCombinedFilters(filtersToApply, filters, FILTER_TYPES.numberPartner);
    this.deleteCombinedFilters(filtersToApply, filters, FILTER_TYPES.initialDeadline);
    this.deleteCombinedFilters(filtersToApply, filters, FILTER_TYPES.updatedDeadline);
    this.deleteCombinedFilters(filtersToApply, filters, FILTER_TYPES.complexityNote);
    this.deleteCombinedFilters(filtersToApply, filters, FILTER_TYPES.noteComplexityBimApplications);
    this.deleteCombinedFilters(filtersToApply, filters, FILTER_TYPES.complexityNoteUsageManagement);
    this.deleteCombinedFilters(filtersToApply, filters, FILTER_TYPES.complexityNoteStandardUses);
    this.deleteCombinedFilters(filtersToApply, filters, FILTER_TYPES.gisBudget);
    this.deleteCombinedFilters(filtersToApply, filters, FILTER_TYPES.carbonBudget);

    const generatePath = (serverField: string, filterType: string, code: string, workTypeCode?: string, unitCode?: string) => {
      return `${encodeURIComponent(serverField)}=${ACCUMULATIVE_NUMBER_FILTERS.includes(filterType)
        ? `${workTypeCode ? encodeURIComponent(workTypeCode) + '|' : ''}${unitCode ? encodeURIComponent(unitCode) : ''}`
        : ''}${encodeURIComponent(code)}`;
    };
    this.bimRequirementsFilters(filtersToApply, filters);
    const filtersPath = Object.keys(filtersToApply).map((filterType) => {
      const serverField = (FILTERS_TYPES as unknown as Record<string, string>)[filterType];
      const filterTypePath = filtersToApply[filterType].map((filter: FilterValue) => {
        return filterType === FILTER_TYPES.taxonomicActivity
          ? generatePath(serverField, filterType, Array.isArray(filter.code) ? filter.code.join(',') : filter.code)
          : generatePath(serverField, filterType, Array.isArray(filter.code) ? filter.code.join(',') : filter.code, filter.workTypeCode, filter.unitCode);
      });
      return filterTypePath.join('&');
    }).filter((f) => f.length);
    return filtersPath?.length ? `idioma=${this.getLanguage()}&${filtersPath.join('&')}` : `idioma=${this.getLanguage()}`;
  }

  async getObrasMapAPI(filters?: FilterState): Promise<ResponseObrasMap | ResponseError> {
    const pathFilters = this.generateFiltersPath(filters);
    const url = `/${ENDPOINTS.v1.obrasMap}?${pathFilters}`;
    return await api.get(url).then((response) => {
      return response.data;
    }).catch((error) => {
      return error.data || { messageError: error.message };
    });
  }

  getObraPropertiesAPI = async (code: string): Promise<ObraProperties> => {
    return await api.get(`/${ENDPOINTS.v1.obrasMap}/${code}?idioma=${this.getLanguage()}`)
      .then((response) => {
        return response.data;
      });
  };

  downloadPDF = async (code: string): Promise<void> => {
    await api.get(`/${ENDPOINTS.v1.obras}/${code}/one-page?idioma=${this.getLanguage()}`)
      .then((response) => {
        // Create blob link to download
        const binaryString = window.atob(response.data);
        const binaryLen = binaryString.length;
        const bytes = new Uint8Array(binaryLen);
        for (let i = 0; i < binaryLen; i++) {
          const ascii = binaryString.charCodeAt(i);
          bytes[i] = ascii;
        }
        const blob = new Blob([bytes], {type: 'application/pdf'});
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${code} - one page.pdf`);

        // Append to html link element page
        document.body.appendChild(link);

        // Start download
        link.click();

        // Clean up and remove the link
        if (link.parentNode) {
          link.parentNode.removeChild(link);
        }
      });
  };


  getProjectCapabilities = async (code: string): Promise<ProjectCapabilities> => {
    return await api.get(`/${ENDPOINTS.v1.permisos}/${code}`)
      .then((response) => {
        return response.data;
      });
  };

  getAutocompleteUrl = async (filter: string, value: string, notLanguage?: boolean) => {
    const maestro = (MASTERS_PATHS as unknown as Record<string, string>)[filter];
    const schema = (MASTERS_RESPONSE_SCHEMA as unknown as Record<string, MasterSchema>)[filter];
    return await api.get(`/${ENDPOINTS.v1.maestros}/${maestro}?texto=${encodeURIComponent(value)}${!notLanguage ? `&idioma=${this.getLanguage()}` : ''}`)
      .then(async (response) => {
        const results = asMasterRecords(response.data?.results);
        const mappedResults = results.map((r) => ({ code: masterString(r, schema.code), name: masterString(r, schema.name) }));

        if (filter !== FILTER_TYPES.nameCode) {
          return mappedResults;
        }

        return await this.enrichCenterAutocompleteResults(mappedResults);
      });
  };

  getAutocompleteWorkSubtypesByCode = async (value: string) => {
    return this.getAutocompleteWorkSubtypes(value, 'codigo');
  };

  getAutocompleteWorkSubtypesByName = async (value: string) => {
    return this.getAutocompleteWorkSubtypes(value, 'texto');
  };

  private getAutocompleteWorkSubtypes = async (value: string, param: string) => {
    const maestro = MASTERS_PATHS.workSubtypeAutocomplete;
    const schema = MASTERS_RESPONSE_SCHEMA.workSubtype;
    return await api.get(`/${ENDPOINTS.v1.maestros}/${maestro}?${param}=${value}&idioma=${this.getLanguage()}`)
      .then((response) => {
        const results = asMasterRecords(response.data?.results);
        return results.map((r) => ({
          id: masterNumber(r, schema.id),
          code: masterString(r, schema.code),
          name: masterString(r, schema.name),
          elements: r[schema.elements],
        }));
      });
  };

  getAutocompleteGroupsClassificationByName = async (value: string) => {
    return this.getAutocompleteGroupsClassification(value, 'texto');
  };

  //Get groups by classification using custom mapper. include
  private getAutocompleteGroupsClassification = async (value: string, param: string) => {
    const maestro = MASTERS_PATHS.classificationGroups;
    const schema = MASTERS_RESPONSE_SCHEMA.classificationGroup;
    return await api.get(`/${ENDPOINTS.v1.maestros}/${maestro}?${param}=${value}&idioma=${this.getLanguage()}`)
      .then((response) => {
        const results = asMasterRecords(response.data?.results);
        return results.map((r) => ({
          id: masterNumber(r, schema.id),
          code: masterString(r, schema.code),
          name: `${masterString(r, schema.code)} ${masterString(r, schema.name)}`,
          elements: Array.isArray(r[schema.elements]) ? (r[schema.elements] as MasterRecord[]).map((sub) => ({ ...sub, [schema.name]: `${masterString(sub, schema.code)} ${masterString(sub, schema.name)}` })) : [],
        }));
      });
  };

  getAutocompleteClassificationGroups = async (value: string) => {
    const maestro = MASTERS_PATHS.classificationGroups;
    const schema = MASTERS_RESPONSE_SCHEMA.classificationGroup;
    return await api.get(`/${ENDPOINTS.v1.maestros}/${maestro}?texto=${encodeURIComponent(value)}&idioma=${this.getLanguage()}`)
      .then((response) => {
        const results = asMasterRecords(response.data?.results);
        return results.map((r) => ({ id: masterNumber(r, schema.id), code: masterString(r, schema.code), name: masterString(r, schema.name) }));
      });
  };

  getClassificationSubgroupsByGroupCode = async (code: string) => {
    const maestro = MASTERS_PATHS.classificationGroups;
    const schema = MASTERS_RESPONSE_SCHEMA.classificationSubgroup;
    return await api.get(`/${ENDPOINTS.v1.maestros}/${maestro}/${code}/subgrupos?idioma=${this.getLanguage()}`)
      .then((response) => {
        const results = asMasterRecords(response.data?.results);
        return results.map((r) => ({ id: masterNumber(r, schema.id), groupCode: masterString(r, schema.groupCode), code: masterString(r, schema.code), name: masterString(r, schema.name) }));
      });
  };

  getClassificationGroupBySubgroupCode = async (code: string) => {
    const maestro = MASTERS_PATHS.classificationGroups;
    const schema = MASTERS_RESPONSE_SCHEMA.classificationGroup;
    return await api.get(`/${ENDPOINTS.v1.maestros}/${maestro}/${code}?idioma=${this.getLanguage()}`)
      .then((response) => {
        const r = (response.data || {}) as MasterRecord;
        return { id: masterNumber(r, schema.id), code: masterString(r, schema.code), name: masterString(r, schema.name) };
      });
  };

  getUnitsByBuildingProcessGroup = async (codigo: string | number) => {
    return this.getUnitsByBuildingProcessGeneric(codigo, 'processGroup');
  };

  getUnitsByBuildingProcess = async (codigo: string | number) => {
    return this.getUnitsByBuildingProcessGeneric(codigo, 'process');
  };

  private getUnitsByBuildingProcessGeneric = async (codigo: string | number, type: string) => {
    const maestro = type === 'processGroup' ? MASTERS_PATHS.processGroup : MASTERS_PATHS.process;
    const schema = MASTERS_RESPONSE_SCHEMA.workUnit;
    return await api.get(`/${ENDPOINTS.v1.maestros}/${maestro}/${codigo}/unidades?idioma=${this.getLanguage()}`)
      .then((response) => {
        const results = asMasterRecords(response.data?.results);
        return results.map((r) => ({ id: masterNumber(r, schema.id), code: masterString(r, schema.code), name: masterString(r, schema.name) }));
      });
  };

  getWorkTypes = async () => {
    const maestro = MASTERS_PATHS.workSubtypeAutocomplete;
    const schema = MASTERS_RESPONSE_SCHEMA.workSubtype;
    return await api.get(`/${ENDPOINTS.v1.maestros}/${maestro}?idioma=${this.getLanguage()}`)
      .then((response) => {
        const results = asMasterRecords(response.data?.results);
        return results.map((r) => ({ id: masterNumber(r, schema.id), code: masterString(r, schema.code), name: masterString(r, schema.name) }));
      });
  };

  getWorkUnits = async () => {
    const maestro = MASTERS_PATHS.workUnitAutocomplete;
    const schema = MASTERS_RESPONSE_SCHEMA.workUnit;
    return await api.get(`/${ENDPOINTS.v1.maestros}/${maestro}?idioma=${this.getLanguage()}`)
      .then((response) => {
        const results = asMasterRecords(response.data?.results);
        return results.map((r) => ({ id: masterNumber(r, schema.id), code: masterString(r, schema.code), name: masterString(r, schema.name) }));
      });
  };

  getClassificationSubgroups = async () => {
    const maestro = MASTERS_PATHS.classificationSubgroups;
    const schema = MASTERS_RESPONSE_SCHEMA.classificationSubgroup;
    return await api.get(`/${ENDPOINTS.v1.maestros}/${maestro}?idioma=${this.getLanguage()}`)
      .then((response) => {
        const results = asMasterRecords(response.data?.results);
        return results.map((r) => ({ id: masterNumber(r, schema.id), groupCode: masterString(r, schema.groupCode), code: masterString(r, schema.code), name: masterString(r, schema.name) }));
      });
  };

  getClassificationGroups = async () => {
    const maestro = MASTERS_PATHS.classificationGroups;
    const schema = MASTERS_RESPONSE_SCHEMA.classificationGroup;
    return await api.get(`/${ENDPOINTS.v1.maestros}/${maestro}?idioma=${this.getLanguage()}`)
      .then((response) => {
        const results = asMasterRecords(response.data?.results);
        return results.map((r) => ({ id: masterNumber(r, schema.id), code: masterString(r, schema.code), name: masterString(r, schema.name) }));
      });
  };

  getAutocompleteWorkUnitsByCode = async (value: string) => {
    return this.getAutocompleteWorkUnits(value, 'codigo');
  };

  getAutocompleteWorkUnitsByName = async (value: string) => {
    return this.getAutocompleteWorkUnits(value, 'texto');
  };

  private getAutocompleteWorkUnits = async (value: string, param: string) => {
    const maestro = MASTERS_PATHS.workUnitAutocomplete;
    const schema = MASTERS_RESPONSE_SCHEMA.workUnit;
    return await api.get(`/${ENDPOINTS.v1.maestros}/${maestro}?${param}=${value}&idioma=${this.getLanguage()}`)
      .then((response) => {
        const results = asMasterRecords(response.data?.results);
        return results.map((r) => ({ id: masterNumber(r, schema.id), code: masterString(r, schema.code), name: masterString(r, schema.name) }));
      });
  };

  getAllChapter = async () => {
    const maestro = MASTERS_PATHS.chapter;
    return await api.get(`/${ENDPOINTS.v1.maestros}/${maestro}?idioma=${this.getLanguage()}`)
      .then((response) => {
        const results = response.data?.results || [];
        return results;
      });
  };

  getChapter = async (value: string) => {
    const maestro = MASTERS_PATHS.chapter;
    return await api.get(`/${ENDPOINTS.v1.maestros}/${maestro}/${value}/capitulos?idioma=${this.getLanguage()}`)
      .then((response) => {
        const results = response.data?.results || [];
        return results;
      });
  };

  searchConstructionProcesses = async (value: string) => {
    const query = new URLSearchParams({ texto: value, idioma: this.getLanguage() });
    const response = await api.get(`/${ENDPOINTS.v1.maestros}/${MASTERS_PATHS.process}?${query}`);
    return response.data?.results || [];
  };

  getAutocompleteProcess = async (value: string) => {
    const maestro = MASTERS_PATHS.processGroup;
    return await api.get(`/${ENDPOINTS.v1.maestros}/${maestro}/${value}/procesos_constructivos?idioma=${this.getLanguage()}`)
      .then((response) => {
        const results = response.data?.results || [];
        return results;
      });
  };

  getGroupChapter = async (value: string) => {
    const maestro = MASTERS_PATHS.chapter;
    return await api.get(`/${ENDPOINTS.v1.maestros}/${maestro}/${value}/grupos_procesos_constructivos?idioma=${this.getLanguage()}`)
      .then((response) => {
        const results = response.data?.results || [];
        return results;
      });
  };

  getObrasListAPI = async (query?: string, filters?: FilterState): Promise<ResponseObrasMap | ResponseError> => {
    const pathFilters = this.generateFiltersPath(filters);
    const url = query ? `/${ENDPOINTS.v1.obrasList}?${query}&${pathFilters}` : `/${ENDPOINTS.v1.obrasList}?${pathFilters}`;
    return await api.get(url)
      .then((response) => {
        return response.data;
      }).catch((error) => {
        return error.data || { messageError: error.message };
      });
  };

  getNumberOfFilteredProjects = async (filters?: FilterState): Promise<number> => {
    const pathFilters = this.generateFiltersPath(filters);
    const url = `/${ENDPOINTS.v1.obrasList}?total=0&${pathFilters}`; // With total=0, the server returns the number of projects but not the projects
    return await api.get(url)
      .then((response) => {
        return response.data.total;
      }).catch((error) => {
        throw error;
      });
  };

  createObrasXLSXJobAPI = async (filters?: FilterState, sections?: string[]): Promise<ExportJob> => {
    const pathFilters = this.generateFiltersPath(filters);
    const sectionFilters = sections?.map((section) => `secciones=${encodeURIComponent(section)}`).join('&');
    const url = `/${ENDPOINTS.v1.export}/jobs?${pathFilters}${sectionFilters ? `&${sectionFilters}` : ''}`;
    return await api.post(url).then((response) => response.data);
  };

  getObrasXLSXEligibilityAPI = async (filters?: FilterState): Promise<boolean> => {
    const pathFilters = this.generateFiltersPath(filters);
    return await api.get(`/${ENDPOINTS.v1.export}/eligibility?${pathFilters}`)
      .then((response) => response.data.allowed === true);
  };

  getObrasXLSXJobsAPI = async (page = 0, perPage = 10): Promise<ExportJobPage> => {
    return await api.get(`/${ENDPOINTS.v1.export}/jobs?page=${page}&perPage=${perPage}`).then((response) => response.data);
  };

  cancelObrasXLSXJobAPI = async (id: number): Promise<ExportJob> => {
    return await api.post(`/${ENDPOINTS.v1.export}/jobs/${id}/cancel`).then((response) => response.data);
  };

  deleteObrasXLSXJobAPI = async (id: number): Promise<void> => {
    await api.delete(`/${ENDPOINTS.v1.export}/jobs/${id}`);
  };

  getObrasXLSXJobAPI = async (id: number): Promise<ExportJob> => {
    return await api.get(`/${ENDPOINTS.v1.export}/jobs/${id}`).then((response) => response.data);
  };

  getObrasXLSXJobDownloadAPI = async (id: number): Promise<ExportJobDownload> => {
    return await api.get(`/${ENDPOINTS.v1.export}/jobs/${id}/download`).then((response) => response.data);
  };

  getObrasXLSXJobFileAPI = async (url: string): Promise<Blob> => {
    return await api.get(url, { responseType: 'blob' }).then((response) => response.data);
  };

  getObrasXLSXAPI = async (filters?: FilterState, sections?: string[]): Promise<void> => {
    const pathFilters = this.generateFiltersPath(filters);
    const sectionFilters = sections?.map((section) => `secciones=${encodeURIComponent(section)}`).join('&');
    const url = `/${ENDPOINTS.v1.export}/xlsx?${pathFilters}${sectionFilters ? `&${sectionFilters}` : ''}`;
    await api.get(url, { responseType: 'blob' })
      .then((response) => {
        // Create blob link to download
        const url = window.URL.createObjectURL(
          new Blob([response.data]),
        );
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute(
          'download',
          `phia-${new Date().toLocaleDateString()}.zip`,
        );

        // Append to html link element page
        document.body.appendChild(link);

        // Start download
        link.click();

        // Clean up and remove the link
        if (link.parentNode) {
          link.parentNode.removeChild(link);
        }
      });
  };

  getWandaProjectAPI = async (code: string): Promise<WandaProject> => {
    return await api.get(`/${ENDPOINTS.v1.obrasFullDetails}/${code}/wanda-project?idioma=${this.getLanguage()}`)
      .then((response) => {
        return response.data.project;
      }).catch((error) => {
        throw new Error(error);
      });
  };

  getPhiaProjectStatusAPI = async (code: string, version: 'viewer' | 'editor'):
    Promise<{ nomEstadoPhia?: string; codEstadoPhia?: number }> => {
    return await api.get(`/${ENDPOINTS.v1.obrasFullDetails}/${code}/phia-status?idioma=${this.getLanguage()}&version=${version}`)
      .then((response) => ({
        nomEstadoPhia: response.data.nomEstadoPhia,
        codEstadoPhia: response.data.codEstadoPhia,
      }))
      .catch((error) => {
        throw new Error(error);
      });
  };

  getObraGeneralDataAPI = async (code: string, project: WandaProject): Promise<GeneralDataDetails> => {
    const projectReference = {
      id: project.id,
      code: project.code,
      officialName: project.officialName,
      name: project.name,
      responsible: project.responsible,
      companyId: project.companyId,
      companyName: project.companyName,
      tenderCode: project.tenderCode,
      countryId: project.countryId,
      countryName: project.countryName,
      zoneId: project.zoneId,
      departmentId: project.departmentId,
      dates: {
        awardDate: project.dates?.awardDate,
      },
      economicData: {
        contractAwardAmountEur: project.economicData?.contractAwardAmountEur,
      },
    };

    return await api.post(`/${ENDPOINTS.v1.obrasFullDetails}/${code}/datos-generales?idioma=${this.getLanguage()}`, {
      project: projectReference,
    })
      .then((response) => {
        return response.data;
      }).catch((error) => {
        throw new Error(error);
      });
  };

  getObraPartnersAPI = async (code: string, projectId: string): Promise<ObraSocios> => {
    return await api.get(`/${ENDPOINTS.v1.obrasFullDetails}/${code}/socios?projectId=${encodeURIComponent(projectId)}&idioma=${this.getLanguage()}`)
      .then((response) => {
        return response.data;
      }).catch((error) => {
        throw new Error(error);
      });
  };

  getObraContractAPI = async (code: string, project: WandaProject): Promise<ObraContrato> => {
    const projectReference = {
      id: project.id,
      code: project.code,
      tenderCode: project.tenderCode,
      status: project.status,
      dates: {
        guarantyTerm: project.dates?.guarantyTerm,
        typeOfPlace: project.dates?.typeOfPlace,
        typeOfPlaceId: project.dates?.typeOfPlaceId,
      },
    };

    return await api.post(`/${ENDPOINTS.v1.obrasFullDetails}/${code}/contrato?idioma=${this.getLanguage()}`, {
      project: projectReference,
    })
      .then((response) => {
        return response.data;
      }).catch((error) => {
        throw new Error(error);
      });
  };

  getObraDatesAPI = async (
    code: string,
    project: WandaProject,
    version: 'viewer' | 'editor',
  ): Promise<DatesDetails> => {
    const projectReference = {
      code: project.code,
      startDate: project.startDate,
      dates: {
        awardDate: project.dates?.awardDate,
        contractSignatureDate: project.dates?.contractSignatureDate,
        replanningDate: project.dates?.replanningDate,
        receptionCertificateDate: project.dates?.receptionCertificateDate,
        expectedEndDate: project.dates?.expectedEndDate,
        updatedExpectedEndDate: project.dates?.updatedExpectedEndDate,
        initialProjectTermInMonths: project.dates?.initialProjectTermInMonths,
        updatedProjectTermInMonths: project.dates?.updatedProjectTermInMonths,
      },
    };

    return await api.post(
      `/${ENDPOINTS.v1.obrasFullDetails}/${code}/fechas-plazos?version=${version}&idioma=${this.getLanguage()}`,
      { project: projectReference },
    )
      .then((response) => response.data)
      .catch((error) => {
        throw new Error(error);
      });
  };

  getObraLocationAPI = async (
    code: string,
    project: WandaProject,
    version: 'viewer' | 'editor',
  ): Promise<LocationDetails> => {
    const projectReference = {
      id: project.id,
      code: project.code,
      countryId: project.countryId,
      countryName: project.countryName,
      location: {
        municipalityId: project.location?.municipalityId,
        zipcode: project.location?.zipcode,
        latitude: project.location?.latitude,
        longitude: project.location?.longitude,
      },
    };

    return await api.post(
      `/${ENDPOINTS.v1.obrasFullDetails}/${code}/localizacion?version=${version}&idioma=${this.getLanguage()}`,
      { project: projectReference },
    )
      .then((response) => response.data)
      .catch((error) => {
        throw new Error(error);
      });
  };

  getObraClientsAPI = async (
    code: string,
    project: WandaProject,
    version: 'viewer' | 'editor',
  ): Promise<ClientsDetails> => {
    const projectReference = {
      id: project.id,
      code: project.code,
      tenderCode: project.tenderCode,
      economicData: {
        contractAwardAmountEur: project.economicData?.contractAwardAmountEur,
        contractBudgetTenderEur: project.economicData?.contractBudgetTenderEur,
      },
    };

    return await api.post(
      `/${ENDPOINTS.v1.obrasFullDetails}/${code}/clientes-colaboradores?version=${version}&idioma=${this.getLanguage()}`,
      { project: projectReference },
    )
      .then((response) => response.data)
      .catch((error) => {
        throw new Error(error);
      });
  };

  getCollaboratingCompaniesAPI = async (
    text: string,
    page = 0,
    perPage = 10,
  ): Promise<{
    data: Array<{ id: string; name: string; source: 'wanda' | 'phia' }>;
    page: number;
    pageSize: number;
    totalPages: number;
    totalElements: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    hasExactMatch?: boolean;
  }> => {
    return await api.get(
      `/${ENDPOINTS.v1.obrasFullDetails}/clientes-colaboradores/empresas-colaboradoras?texto=${encodeURIComponent(text)}&page=${page}&perPage=${perPage}&idioma=${this.getLanguage()}`,
    )
      .then((response) => ({
        ...response.data,
        data: filterMeaningfulOptions(response.data.data),
      }))
      .catch((error) => {
        throw new Error(error);
      });
  };

  getObraDescriptionAPI = async (
    code: string,
    project: WandaProject,
    version: 'viewer' | 'editor',
  ): Promise<DescriptionDetails> => {
    const projectReference = {
      id: project.id,
      code: project.code,
      tenderCode: project.tenderCode,
    };

    return await api.post(
      `/${ENDPOINTS.v1.obrasFullDetails}/${code}/descripcion?version=${version}&idioma=${this.getLanguage()}`,
      { project: projectReference },
    )
      .then((response) => response.data)
      .catch((error) => {
        throw new Error(error);
      });
  };

  getObraEsgAPI = async (
    code: string,
    project: WandaProject,
    version: 'viewer' | 'editor',
  ): Promise<EsgDetails> => {
    const projectReference = {
      id: project.id,
      code: project.code,
      tenderCode: project.tenderCode,
    };

    return await api.post(
      `/${ENDPOINTS.v1.obrasFullDetails}/${code}/esg?version=${version}&idioma=${this.getLanguage()}`,
      { project: projectReference },
    )
      .then((response) => response.data)
      .catch((error) => {
        throw new Error(error);
      });
  };


  getObraTechnicalAspectsAPI = async (
    code: string,
    project: WandaProject,
    version: 'viewer' | 'editor',
  ): Promise<TechnicalAspectsDetails> => {
    const projectReference = {
      id: project.id,
      code: project.code,
    };

    return await api.post(
      `/${ENDPOINTS.v1.obrasFullDetails}/${code}/aspectos-tecnicos?version=${version}&idioma=${this.getLanguage()}`,
      { project: projectReference },
    )
      .then((response) => response.data)
      .catch((error) => {
        throw new Error(error);
      });
  };

  getObraClassificationAPI = async (code: string, projectId: string): Promise<ObraClasificacion> => {
    return await api.get(`/${ENDPOINTS.v1.obrasFullDetails}/${code}/clasificacion?projectId=${encodeURIComponent(projectId)}&idioma=${this.getLanguage()}`)
      .then((response) => {
        return response.data;
      }).catch((error) => {
        throw new Error(error);
      });
  };

  getObraBimAPI = async (code: string, projectId: string): Promise<ObraBim> => {
    return await api.get(`/${ENDPOINTS.v1.obrasFullDetails}/${code}/bim?projectId=${encodeURIComponent(projectId)}&idioma=${this.getLanguage()}`)
      .then((response) => {
        return response.data;
      }).catch((error) => {
        throw new Error(error);
      });
  };

  getObraBudgetAPI = async (code: string, project: WandaProject): Promise<ObraPresupuesto> => {
    const projectReference = {
      id: project.id,
      code: project.code,
      dates: project.dates,
      economicData: project.economicData,
    };

    return await api.post(`/${ENDPOINTS.v1.obrasFullDetails}/${code}/budget?idioma=${this.getLanguage()}`, {
      project: projectReference,
    })
      .then((response) => {
        return response.data;
      }).catch((error) => {
        throw new Error(error);
      });
  };

  getGroups = async () => {
    const maestro = MASTERS_PATHS.workType;
    const schema = MASTERS_RESPONSE_SCHEMA.workType;
    return await api.get(`/${ENDPOINTS.v1.maestros}/${maestro}?idioma=${this.getLanguage()}`)
      .then((response) => {
        const results = asMasterRecords(response.data?.results);
        return results.map((r) => ({ id: masterNumber(r, schema.id), code: masterString(r, schema.code), name: masterString(r, schema.name) }));
      });
  };

  getSubtypes = async (groupCode: string) => {
    const maestro = MASTERS_PATHS.workType;
    const subMaestro = MASTERS_PATHS.workSubtype;
    const schema = MASTERS_RESPONSE_SCHEMA.workSubtype;
    return await api.get(`/${ENDPOINTS.v1.maestros}/${maestro}/${groupCode}/${subMaestro}?idioma=${this.getLanguage()}`)
      .then((response) => {
        const results = asMasterRecords(response.data?.results);
        return results.map((r) => ({ code: masterString(r, schema.code), name: masterString(r, schema.name) }));
      });
  };

  getMagnitudes = async (subTypeCode: string) => {
    const maestro = MASTERS_PATHS.workSubtype;
    const subMaestro = MASTERS_PATHS.magnitudes;
    const schema = MASTERS_RESPONSE_SCHEMA.workSubtype;
    return await api.get(`/${ENDPOINTS.v1.maestros}/${maestro}/${subTypeCode}/${subMaestro}?idioma=${this.getLanguage()}`)
      .then((response) => {
        const results = asMasterRecords(response.data?.results);
        return results.map((r) => ({ id: masterNumber(r, schema.id), name: masterString(r, schema.name) }));
      });
  };

  private getMasters = async (maestro: string, schema: MasterSchema) => {
    return await api.get(`/${ENDPOINTS.v1.maestros}/${maestro}?idioma=${this.getLanguage()}`)
      .then((response) => {
        const results = asMasterRecords(response.data?.results);
        return results.map((r) => ({ code: masterString(r, schema.code), name: masterString(r, schema.name) }));
      });
  };

  getCentersByText = async (text: string) => {
    const maestro = MASTERS_PATHS.nameCode;
    const schema = MASTERS_RESPONSE_SCHEMA.nameCode;
    return await api.get(`/${ENDPOINTS.v1.maestros}/${maestro}?texto=${encodeURIComponent(text)}&idioma=${this.getLanguage()}`)
      .then(async (response) => {
        const results = asMasterRecords(response.data?.results);
        const mappedResults = results.map((r) => ({ code: masterString(r, schema.code), name: masterString(r, schema.name) }));
        return await this.enrichCenterAutocompleteResults(mappedResults);
      }).catch(() => {
        return [];
      });
  };

  getMasterData = async (masterPath: string, masterSchema: string) => {
    const maestro = (MASTERS_PATHS as unknown as Record<string, string>)[masterPath];
    const schema = (MASTERS_RESPONSE_SCHEMA as unknown as Record<string, MasterSchema>)[masterSchema];
    return await this.getMasters(maestro, schema);
  };

  getContractStatus = async () => {
    const maestro = MASTERS_PATHS.contractStatus;
    const schema = MASTERS_RESPONSE_SCHEMA.contractStatus;
    return await this.getMasters(maestro, schema);
  };
  getContractTypes = async () => {
    const maestro = MASTERS_PATHS.contractTypes;
    const schema = MASTERS_RESPONSE_SCHEMA.contractTypes;
    return await this.getMasters(maestro, schema);
  };

  getContractModels = async () => {
    const maestro = MASTERS_PATHS.contractModels;
    const schema = MASTERS_RESPONSE_SCHEMA.contractModels;
    return await this.getMasters(maestro, schema);
  };

  getContractScope = async () => {
    const maestro = MASTERS_PATHS.contractScope;
    const schema = MASTERS_RESPONSE_SCHEMA.contractScope;
    return await this.getMasters(maestro, schema);
  };

  getFormPayment = async () => {
    const maestro = MASTERS_PATHS.contractFormPayment;
    const schema = MASTERS_RESPONSE_SCHEMA.contractFormPayment;
    return await this.getMasters(maestro, schema);
  };

  getSourceFinance = async () => {
    const maestro = MASTERS_PATHS.contractSourceFinance;
    const schema = MASTERS_RESPONSE_SCHEMA.contractSourceFinance;
    return await this.getMasters(maestro, schema);
  };

  getBusinessActivities = async () => {
    const maestro = MASTERS_PATHS.businessActivities;
    const schema = MASTERS_RESPONSE_SCHEMA.businessActivities;
    return await this.getMasters(maestro, schema);
  };

  getMaturityLevel = async () => {
    const maestro = MASTERS_PATHS.maturityLevel;
    const schema = MASTERS_RESPONSE_SCHEMA.maturityLevel;
    return await this.getMasters(maestro, schema);
  };

  getCategorization = async () => {
    const maestro = MASTERS_PATHS.categorization;
    const schema = MASTERS_RESPONSE_SCHEMA.categorization;
    return await this.getMasters(maestro, schema);
  };

  getRequirements = async () => {
    const maestro = MASTERS_PATHS.requirements;
    const schema = MASTERS_RESPONSE_SCHEMA.requirements;
    return await api.get(`/${ENDPOINTS.v1.maestros}/${maestro}?idioma=${this.getLanguage()}`)
      .then((response) => {
        const results = asMasterRecords(response.data?.results);
        return results.map((r) => ({ codigos: (r[schema.codigos] as number[]) || [], code: masterString(r, schema.code), name: masterString(r, schema.name) }));
      });
  };

  getTaxonomicObjectives = async () => {
    const maestro = MASTERS_PATHS.taxonomicObjective;
    const schema = MASTERS_RESPONSE_SCHEMA.taxonomicObjective;
    return await this.getMasters(maestro, schema);
  };

  getSocialCategories = async () => {
    const maestro = MASTERS_PATHS.socialCategories;
    const schema = MASTERS_RESPONSE_SCHEMA.socialCategories;
    return await this.getMasters(maestro, schema);
  };

  getGISPercentage = async () => {
    const maestro = MASTERS_PATHS.gisPercentage;
    const schema = MASTERS_RESPONSE_SCHEMA.gisPercentage;
    return await this.getMasters(maestro, schema);
  };

  getPhotoTypes = async (): Promise<TipoFoto[]> => {
    const maestro = MASTERS_PATHS.photoTypes;
    const schema = MASTERS_RESPONSE_SCHEMA.photoType;
    const response = await api.get(`/${ENDPOINTS.v1.maestros}/${maestro}?idioma=${this.getLanguage()}`);
    const results = asMasterRecords(response.data?.results);
    return results.map((r) => ({ id: masterNumber(r, schema.id), tipo: masterString(r, schema.name) }));
  };

  getProtectedSpaces = async (): Promise<Array<SelectOption & { code: string; name: string }>> => {
    const maestro = MASTERS_PATHS.protectedSpace;
    const schema = MASTERS_RESPONSE_SCHEMA.protectedSpace;
    return await api.get(`/${ENDPOINTS.v1.maestros}/${maestro}?idioma=${this.getLanguage()}`)
      .then((response) => {
        const results = asMasterRecords(response.data?.results);
        return results.map((r) => ({ id: masterNumber(r, schema.code), nombre: masterString(r, schema.name), code: masterString(r, schema.code), name: masterString(r, schema.name) }));
      }).catch(() => {
        return [];
      });
  };

  getProtectedSpacesLocations = async (): Promise<Array<SelectOption & { code: string; name: string }>> => {
    const maestro = MASTERS_PATHS.protectedSpaceLocations;
    const schema = MASTERS_RESPONSE_SCHEMA.protectedSpace;
    return await api.get(`/${ENDPOINTS.v1.maestros}/${maestro}?idioma=${this.getLanguage()}`)
      .then((response) => {
        const results = asMasterRecords(response.data?.results);
        return results.map((r) => ({ id: masterNumber(r, schema.code), nombre: masterString(r, schema.name), code: masterString(r, schema.code), name: masterString(r, schema.name) }));
      }).catch(() => {
        return [];
      });
  };

  getDangerLevels = async (): Promise<Array<SelectOption & { code: string; name: string }>> => {
    const maestro = MASTERS_PATHS.dangerLevels;
    const schema = MASTERS_RESPONSE_SCHEMA.dangerLevels;
    const response = await api.get(`/${ENDPOINTS.v1.maestros}/${maestro}?idioma=${this.getLanguage()}`);
    const results = asMasterRecords(response.data?.results);
    return results.map((r) => ({ id: masterNumber(r, schema.code), nombre: masterString(r, schema.name), code: masterString(r, schema.code), name: masterString(r, schema.name) }));
  };

  getProtectionZones = async (): Promise<Array<SelectOption & { code: string; name: string }>> => {
    const maestro = MASTERS_PATHS.protectionZones;
    const schema = MASTERS_RESPONSE_SCHEMA.protectionZones;
    const response = await api.get(`/${ENDPOINTS.v1.maestros}/${maestro}?idioma=${this.getLanguage()}`);
    const results = asMasterRecords(response.data?.results);
    return results.map((r) => ({ id: masterNumber(r, schema.code), nombre: masterString(r, schema.name), code: masterString(r, schema.code), name: masterString(r, schema.name) }));
  };

  getClientStatusMasters = async (): Promise<SelectOption[]> => {
    const maestro = MASTERS_PATHS.clientStatus;
    const schema = MASTERS_RESPONSE_SCHEMA.clientStatus;
    return await api.get(`/${ENDPOINTS.v1.maestros}/${maestro}?idioma=${this.getLanguage()}`)
      .then((response) => {
        const results = asMasterRecords(response.data?.results);
        return results.map((r) => ({ id: masterNumber(r, schema.code), nombre: masterString(r, schema.name), code: masterString(r, schema.code), name: masterString(r, schema.name) }));
      }).catch(() => {
        return [];
      });
  };

  getCDE = async (t: TFunction<'translations'>): Promise<OptionBoolean[]> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const content: OptionBoolean[] = [
          { code: true, name: `${t('optionBoolean.yes')}`},
          { code: false, name: `${t('optionBoolean.no')}`},
        ];
        resolve(content);
      }, 100);
    });
  };

  getTypeBimApplicationsMasters = async (): Promise<Option[]> => {
    const maestro = MASTERS_PATHS.typesBimApplications;
    const schema = MASTERS_RESPONSE_SCHEMA.typesBimApplications;
    return await api.get(`/${ENDPOINTS.v1.maestros}/${maestro}?idioma=${this.getLanguage()}`)
      .then((response) => {
        const results = asMasterRecords(response.data?.results);
        return results.map((r) => ({ code: masterString(r, schema.code), name: masterString(r, schema.name) }));
      }).catch(() => {
        return [];
      });
  };

  save = async (data: unknown, draft = false) => {
    const url = `/${ENDPOINTS.v1.obras}`;
    return await api.post(`${draft ? `${url}/draft` : url}`, data)
      .then((response) => {
        if (typeof response.data === 'boolean') {
          return response.data;
        }
        return response.data?.results ?? response.data;
      }).catch(err => {
        console.error(err);
        return false;
      });
  };

  approve = async (data: unknown) => {
    const url = `/${ENDPOINTS.v1.obras}`;
    return await api.post(`${url}/validate`, data)
      .then((response) => {
        return response.data;
      }).catch(err => {
        console.error(err);
      });
  };

  requestValidation = async (data: unknown, draft = false) => {
    const url = `/${ENDPOINTS.v1.obras}`;
    return await api.post(`${draft ? `${url}/requestValidation` : url}`, data)
      .then((response) => {
        return response.data;
      }).catch(err => {
        console.error(err);
      });
  };

  reject = async (data: unknown) => {
    const url = `/${ENDPOINTS.v1.obras}`;
    return await api.post(`${url}/reject`, data)
      .then((response) => {
        return response.data;
      }).catch(err => {
        console.error(err);
      });
  };

  uploadFile = async (file: Blob, cod_centro: string, type: string) => {
    const data = new FormData();
    data.append('file', file);
    const url = `/${ENDPOINTS.v1.obras}`;
    return await api.post(`${url}/${cod_centro}/ficheros?tipo=${type === 'image' ? 'imagen' : 'kmz'}`, data)
      .then((response) => {
        const responseUrl = response.data?.url || '';
        return { statusCode: 200, url: responseUrl };
      }).catch((error) => {
        return error.response.data;
      });
  };

  getNotificationsAPI = async (
    diasAntiguedad?: number,
    cursor?: string,
    signal?: AbortSignal,
  ): Promise<NotificationPage> => {
    const url = `/${ENDPOINTS.v1.notificaciones}`;
    const params = new URLSearchParams({ idioma: this.getLanguage() });
    if (diasAntiguedad) params.set('diasAntiguedad', String(diasAntiguedad));
    if (cursor) params.set('cursor', cursor);
    const response = await api.get<NotificationPage>(`${url}?${params.toString()}`, { signal });
    return response.data;
  };

  getNotificationSseTicket = async (): Promise<string> => {
    const url = `/${ENDPOINTS.v1.notificaciones}/sse-ticket`;
    const response = await api.get<{ ticket: string }>(url);
    return response.data.ticket;
  };

  setNotificationRead = async (data: unknown) => {
    const url = `/${ENDPOINTS.v1.notificaciones}`;
    return await api.post(`${url}/read`, data)
      .then(() => {
        return { statusCode: 200 };
      }).catch(() => {
        return { statusCode: 500 };
      });
  };

  getUsers = async (pageNumber = 0, search = ''): Promise<AdminUsersPage | { statusCode: number }> => {
    const searchParam = search.trim() ? `&search=${encodeURIComponent(search.trim())}` : '';
    const url = `/${ENDPOINTS.admin.general}?idioma=${this.getLanguage()}&page=${pageNumber}${searchParam}`;
    return await api.get(url)
      .then((response) => {
        return response.data;
      }).catch((error) => {
        return error.response.data;
      });
  };

  getPermissionsByUserEmail = async (userEmail: string) => {
    const url = `/${ENDPOINTS.admin.permissions}?idioma=${this.getLanguage()}&email=${userEmail}`;
    return await api.get(url)
      .then((response) => {
        return response.data;
      }).catch((error) => {
        return error.response.data;
      });
  };

  getPermissionProjectLabels = async (projectIds: string[]): Promise<Record<string, string>> => {
    return await api.post(`/${ENDPOINTS.admin.permissionProjectLabels}`, { projectIds })
      .then((response) => response.data);
  };

  getAutocompleteMastersUrl = async (filter: string, value: string) => {
    const maestro = (MASTERS_PATHS as unknown as Record<string, string>)[filter];
    const schema = (MASTERS_RESPONSE_SCHEMA as unknown as Record<string, MasterSchema>)[filter];
    return await api.get(`/${ENDPOINTS.v1.maestros}/${maestro}?texto=${value}&idioma=${this.getLanguage()}`)
      .then((response) => {
        const results = asMasterRecords(response.data?.results);
        return results.map((r) => ({ code: masterString(r, schema.code), name: masterString(r, schema.codname) }));
      });
  };

  getAssignmentCandidates = async (search: string, page = 0): Promise<AdminAssignmentCandidatesPage> => {
    const url = `/${ENDPOINTS.admin.assignmentCandidates}?search=${encodeURIComponent(search.trim())}&page=${page}`;
    return api.get(url).then((response) => response.data);
  };

  getAllNoPhiaUsers = async (search = ''): Promise<Usuario[] | { statusCode: number }> => {
    let url = `/${ENDPOINTS.admin.noPhia}`;
    if (search.trim()) url += `?search=${encodeURIComponent(search.trim())}`;

    return await api
      .get(url)
      .then((response) => response.data)
      .catch((error) => error.response.data);
  };

  getProjectByCodeName = async (newValue: string) => {
    const url = `/${ENDPOINTS.admin.projects}?idioma=${this.getLanguage()}&texto=${newValue}`;
    return await api.get(url)
      .then((response) => {
        return response.data?.results || [];
      }).catch((error) => {
        return error.response.data;
      });
  };

  getUsersByProject = async (project: CenterParams) => {
    let params = `&centro=${project.codCentro}`;
    params = `${params}&supercentro=${project.codSupercentro}`;
    params = `${params}&zona=${project.codZona}`;
    params = `${params}&empresa=${project.codActividadNegocio}`;
    if (project.id) params = `${params}&objectId=${encodeURIComponent(project.id)}`;
    if (project.supercenterId) params = `${params}&supercenterId=${encodeURIComponent(project.supercenterId)}`;
    if (project.zoneId) params = `${params}&zoneId=${encodeURIComponent(project.zoneId)}`;
    const url = `/${ENDPOINTS.admin.users}?idioma=${this.getLanguage()}${params}`;
    return await api.get(url)
      .then((response) => {
        return response.data || [];
      }).catch((error) => {
        return error.response.data;
      });
  };

  getPendingActionsAPI = async (cursor: string | undefined, filter: string, onlyPending: boolean, signal?: AbortSignal) => {
    const url = `/${ENDPOINTS.v1.notificaciones}/acciones`;
    const params = new URLSearchParams({ filter, onlyPending: String(onlyPending), idioma: this.getLanguage() });
    if (cursor) params.set('cursor', cursor);
    const response = await api.get(`${url}?${params.toString()}`, { signal });
    return response.data;
  };

  getPendingActionCountsAPI = async (signal?: AbortSignal): Promise<Record<string, number>> => {
    const url = `/${ENDPOINTS.v1.notificaciones}/acciones/counts`;
    const response = await api.get<Record<string, number>>(`${url}?idioma=${this.getLanguage()}`, { signal });
    return response.data;
  };

  editRole = async (id: string | undefined, email: string | undefined, rol: string | undefined, codCentro: string, codSupercentro: string, codZona: string, codEmpresa: string) => {
    const url = `/${ENDPOINTS.admin.permissions}?idioma=${this.getLanguage()}`;
    const data = { id, email, rol, codCentro, codSupercentro, codZona, codEmpresa };
    return await api.patch(url, data)
      .then((response) => {
        return response.data;
      }).catch((error) => {
        return { error };
      });
  };

  addRole = async (id: string | undefined, email: string | undefined, rol: string | undefined, codCentro: string, codSupercentro: string, codZona: string, codEmpresa: string) => {
    const url = `/${ENDPOINTS.admin.permissions}?idioma=${this.getLanguage()}`;
    const data = { id, email, rol, codCentro, codSupercentro, codZona, codEmpresa };
    return await api.post(url, data)
      .then((response) => {
        return response.data;
      }).catch((error) => {
        return { error };
      });
  };


  deleteRole = async (id: string, userEmail: string) => {
    const url = `/${ENDPOINTS.admin.permissions}/${id}?email=${encodeURIComponent(userEmail)}`;
    return await api.delete(url)
      .then((response) => {
        return response.data;
      }).catch((error) => {
        return { error };
      });
  };

  getZones = async () => {
    const maestro = MASTERS_PATHS.allZones;
    return await api.get(`/${ENDPOINTS.v1.maestros}/${maestro}?idioma=${this.getLanguage()}`)
      .then((response) => {
        const results = response.data?.results || [];
        return results;
      });
  };

  getZonesPage = async (text = '', page = 0, perPage = 50, companyCode = '0008'): Promise<MasterPage<RemoteMasterOption>> => {
    const maestro = MASTERS_PATHS.zone;
    const url = `/${ENDPOINTS.v1.maestros}/${maestro}?texto=${encodeURIComponent(text)}&idioma=${this.getLanguage()}&page=${page}&perPage=${perPage}&empresa=${encodeURIComponent(companyCode)}`;
    return await api.get(url).then((response) => ({
      ...response.data,
      results: (response.data?.results || []).map((result: { codZona: string; nomZona: string }) => ({
        code: result.codZona,
        nombre: `${result.codZona} ${result.nomZona}`,
      })),
    }));
  };

  getSupercentersPage = async (
    zoneCode: string,
    text = '',
    page = 0,
    perPage = 50,
  ): Promise<MasterPage<RemoteMasterOption>> => {
    const maestro = MASTERS_PATHS.supercenter;
    const url = `/${ENDPOINTS.v1.maestros}/${maestro}?zona=${encodeURIComponent(zoneCode)}&texto=${encodeURIComponent(text)}&idioma=${this.getLanguage()}&page=${page}&perPage=${perPage}`;
    return await api.get(url).then((response) => ({
      ...response.data,
      results: (response.data?.results || []).map((result: { codSupercentro: string; nomSupercentro: string }) => ({
        code: result.codSupercentro,
        nombre: `${result.codSupercentro} ${result.nomSupercentro}`,
      })),
    }));
  };

  getCentersPage = async (
    supercenterCode: string,
    text = '',
    page = 0,
    perPage = 50,
  ): Promise<MasterPage<RemoteMasterOption>> => {
    const maestro = MASTERS_PATHS.nameCode;
    const url = `/${ENDPOINTS.v1.maestros}/${maestro}?supercentro=${encodeURIComponent(supercenterCode)}&texto=${encodeURIComponent(text)}&idioma=${this.getLanguage()}&page=${page}&perPage=${perPage}`;
    return await api.get(url).then((response) => ({
      ...response.data,
      results: (response.data?.results || []).map((result: { codCentro: string; nomCentro: string }) => ({
        code: result.codCentro,
        nombre: `${result.codCentro} ${result.nomCentro}`,
      })),
    }));
  };

  getSupercenterByZone = async (value: string) => {
    const maestro = MASTERS_PATHS.supercenter;
    const schema = MASTERS_RESPONSE_SCHEMA.supercenter;
    return await api.get(`/${ENDPOINTS.v1.maestros}/${maestro}?texto=&idioma=${this.getLanguage()}&zona=${encodeURIComponent(value)}`)
      .then((response) => {
        const results = asMasterRecords(response.data?.results);
        return results.map((r) => ({ id: masterNumber(r, schema.code), nombre: masterString(r, schema.name) }));
      });
  };


  getCertificates = async () => {
    const maestro = MASTERS_PATHS.certificates;
    return await api.get(`/${ENDPOINTS.v1.maestros}/${maestro}?idioma=${this.getLanguage()}`)
      .then((response) => {
        return response;
      }).catch((error) => {
        return error.response.data;
      });
  };


  getCertificatesLevel = async () => {
    const maestro = MASTERS_PATHS.certificatesLevel;
    return await api.get(`/${ENDPOINTS.v1.maestros}/${maestro}?idioma=${this.getLanguage()}`)
      .then((response) => {
        return response;
      }).catch((error) => {
        return error.response.data;
      });
  };

  getSustainabilityCertificateOptions = async (): Promise<Option[]> => {
    const response = await this.getCertificates();
    const results = asMasterRecords(response?.data?.results);
    return results.map((result) => ({
      code: masterString(result, 'codigo'),
      name: masterString(result, 'nombre'),
    }));
  };

  getSustainabilityCertificateLevelOptions = async (certificateCode: string): Promise<Option[]> => {
    if (!certificateCode) return [];

    const response = await this.getCertificatesLevel();
    const results = asMasterRecords(response?.data?.results);
    return results
      .filter((result) => masterString(result, 'codigo') === certificateCode)
      .filter((result) => masterString(result, 'codigoNivel') !== '')
      .map((result) => ({
        code: `${certificateCode}-${masterString(result, 'codigoNivel')}`,
        name: masterString(result, 'nombre'),
      }));
  };

}

export default ObrasAPI;
